import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CdkDrag, CdkDragDrop, CdkDropList } from '@angular/cdk/drag-drop';
import { Subscription } from 'rxjs';
import { AppNotificationCounts, SSENotificationService } from '../../../services/sse-notification.service';
import { MainMenuService } from '../../../services/main-menu.service';
import { MenuItem, MenuItemClickType } from '../../main/main-menu/menu-item/menu-item';
import { UserService } from '../../../services/user-service.service';
import { checkIsWebView } from '../../../helpers/screen-utils';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { RtI18nDirective } from '../shared/runtime-translation.directive';
import { AiChatWidgetService } from '../shared/ai/ai-chat-widget/ai-chat-widget.service';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { GuardianLinkService, LinkedGuardian } from '../../../services/guardian-link.service';
import { UserProfileService } from '../../../services/user-profile.service';
import { AccountSwitchService } from '../../../services/account-switch.service';
import { User } from '../../models/user.model';
import { UserImageComponent } from '../shared/user-image/user-image.component';


@Component({
    selector: 'apps-footer',
    templateUrl: './apps-footer.component.html',
    styleUrl: './apps-footer.component.scss',
    imports: [CommonModule, RouterLink, RouterLinkActive, RtI18nDirective, CdkDropList, CdkDrag, MatMenu, MatMenuItem, MatMenuTrigger, UserImageComponent]
})
export class AppsFooterComponent implements OnInit, OnDestroy {
  /** Only the main menu sets this; the bar is read-only everywhere else. */
  @Input() isEditMode: boolean = false;

  /** True on the main menu itself, where the + slot can toggle edit mode in place. */
  @Input() isMainMenuView: boolean = false;

  /** Asks the hosting main menu to enter edit mode (fired by the + slot). */
  @Output() toggleEditMode = new EventEmitter<void>();

  /** The user's nav-row apps, after Home. Role filtering already happened server-side. */
  headerApps: MenuItem[] = [];
  appCounts: AppNotificationCounts = {};
  readonly MAX_HEADER_APPS = MainMenuService.MAX_HEADER_APPS;

  /** The grid tile currently in flight (edit mode only); drives the can't-pin messaging. */
  draggingGridApp: MenuItem | null = null;

  /** Drives the "Switch accounts" footer-menu option; loaded like the web header's switcher. */
  linkedGuardian: LinkedGuardian | undefined = undefined;
  impersonatedUser: User | undefined = undefined;

  isConnected = false;
  isConnecting = false;
  private subscriptions: Subscription[] = [];

  public get showAI(): boolean {
    const profile = this.userService.CurrentUserProfile;
    return (profile?.ShowAI ?? false) && profile?.UserType !== 'Student';
  }

  /** Currently viewing the app as one's own linked parent account (via "Switch to Parent"). */
  get isParentAccountView(): boolean {
    return sessionStorage.getItem('parent_account_switch') === 'true';
  }

  /** Staff member (not currently impersonating) who has linked their own parent account. */
  get showSwitchToParent(): boolean {
    return this.userService.CurrentUserProfile?.UserType === 'Staff'
      && !this.impersonatedUser
      && !!this.linkedGuardian;
  }

  /** Whether the footer help button should offer an account switch alongside the help bot. */
  get canSwitchAccounts(): boolean {
    return this.showSwitchToParent || this.isParentAccountView;
  }

  /** Current profile, for the footer switcher avatar (shows the identity you're currently in). */
  get currentUser(): User | undefined {
    return this.userService.CurrentUserProfile;
  }

  openAIMenu(): void {
    const current = this.aiChatWidgetService.getState();
    this.aiChatWidgetService.updateState({ showAssistantMenu: !current.showAssistantMenu });
  }

  /** Loads the staff member's linked parent account (if any) to drive the switch menu item. */
  checkLinkedGuardian(): void {
    if (this.userService.CurrentUserProfile?.UserType !== 'Staff') {
      this.linkedGuardian = undefined;
      return;
    }

    this.guardianLinkService.getLinkedGuardian().subscribe({
      next: (linked) => this.linkedGuardian = linked ?? undefined,
      error: () => this.linkedGuardian = undefined
    });
  }

  loadImpersonatedUser(): void {
    this.userProfileService.getImpersonatedUserProfile().subscribe(
      (result: User) => {
        // Active impersonation with no session flag = a new browser session; clear the stale one.
        if (result && !sessionStorage.getItem('impersonation_session_active')) {
          this.accountSwitch.switchToStaff('/mainmenu');
          return;
        }
        sessionStorage.setItem('impersonation_session_active', 'true');
        this.impersonatedUser = result;
      }
    );
  }

  /** Staff -> their linked parent account, returning to the current page after the reload. */
  switchToParent(): void {
    this.accountSwitch.switchToParent(window.location.pathname + window.location.search);
  }

  /** Parent-account view -> back to staff, returning to the current page after the reload. */
  switchToStaff(): void {
    this.accountSwitch.switchToStaff(window.location.pathname + window.location.search);
  }

  constructor(private userService: UserService,
              private notificationService: SSENotificationService,
              private aiChatWidgetService: AiChatWidgetService,
              private guardianLinkService: GuardianLinkService,
              private userProfileService: UserProfileService,
              private accountSwitch: AccountSwitchService,
              private router: Router,
              private mainMenuService: MainMenuService) { }

  /** Badge count for a nav-row app, from the polling payload. */
  badgeFor(app: MenuItem): number {
    return this.appCounts[app.Id] ?? 0;
  }

  /** Keeps the guided tour's #tour-messages / #tour-posts / #tour-notifications anchors alive. */
  tourAnchorId(app: MenuItem): string | null {
    return app.ClickPath ? 'tour-' + app.ClickPath : null;
  }

  /**
   * Router target for a nav-row app. Student-type apps (Grades/Attendance) live under the
   * logged-in student's own page rather than a top-level /apps route — the same fallback
   * menu-item uses on a student's main menu. Null (no navigation) while editing.
   */
  navRoute(app: MenuItem): (string | number)[] | null {
    if (this.isEditMode) {
      return null;
    }
    if (app.ClickType === MenuItemClickType.Student) {
      const profile = this.userService.CurrentUserProfile;
      return profile?.UserType === 'Student' ? ['/apps/students', profile.Id, app.ClickPath ?? ''] : null;
    }
    return ['/apps', app.ClickPath ?? ''];
  }

  /**
   * Refuses drops the row can't take, so a full row or an app with no monochrome icon simply
   * won't accept the tile instead of silently dropping it on the floor.
   */
  canDropInHeader = (drag: CdkDrag): boolean => {
    const app = drag.data as MenuItem | undefined;
    if (!app || !MainMenuService.canPinToHeader(app)) {
      return false;
    }
    return this.headerApps.some(a => a.Id === app.Id)
        || this.headerApps.length < this.MAX_HEADER_APPS;
  };

  /**
   * Why the bar would refuse the tile being dragged, or null when it wouldn't. Mirrors
   * canDropInHeader, which can only silently reject — this powers the visible explanation.
   */
  get pinBlockReason(): 'not-pinnable' | 'bar-full' | null {
    const app = this.draggingGridApp;
    if (!this.isEditMode || !app) {
      return null;
    }
    if (!MainMenuService.canPinToHeader(app)) {
      return 'not-pinnable';
    }
    // Moving an app that's already in the row never needs a free slot.
    if (!this.headerApps.some(a => a.Id === app.Id) && this.headerApps.length >= this.MAX_HEADER_APPS) {
      return 'bar-full';
    }
    return null;
  }

  onHeaderDrop(event: CdkDragDrop<unknown>): void {
    const app = event.item.data as MenuItem | undefined;
    if (app) {
      // Deliberately not transferArrayItem: pinning is a copy, so the tile stays on the grid.
      this.mainMenuService.pinAppToHeader(app.Id, event.currentIndex);
    }
  }

  removeFromHeader(app: MenuItem, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.mainMenuService.unpinAppFromHeader(app.Id);
  }

  /** Whether to show the spare slot's + affordance. */
  get showAddSlot(): boolean {
    return !this.isEditMode && this.headerApps.length < this.MAX_HEADER_APPS;
  }

  /** One entry per unpinned slot, so edit mode can draw a dotted box for each open spot. */
  get emptySlots(): number[] {
    return Array.from({ length: Math.max(0, this.MAX_HEADER_APPS - this.headerApps.length) }, (_, i) => i);
  }

  /** Slots the drop list renders — pinned apps plus placeholders — so the bar shares width evenly. */
  get navSlotCount(): number {
    if (this.isEditMode) {
      // Pinned apps plus one dotted box per open spot always fill the row.
      return this.MAX_HEADER_APPS;
    }
    return (this.headerApps.length + (this.showAddSlot ? 1 : 0)) || 1;
  }

  /** The spare-slot + : enter edit mode in place on the main menu, or head there and open it. */
  onAddAppClick(): void {
    if (this.isMainMenuView) {
      this.toggleEditMode.emit();
    } else {
      this.router.navigate(['/mainmenu'], { queryParams: { editApps: 1 } });
    }
  }

  async ngOnInit() {
    // Before the isWebView guard: checkIsWebView() can be true at first paint on a tablet-width
    // browser, and the nav row should still paint from cache.
    this.subscriptions.push(
      this.mainMenuService.headerApps$.subscribe(apps => this.headerApps = apps)
    );
    this.subscriptions.push(
      this.mainMenuService.draggingGridApp$.subscribe(app => this.draggingGridApp = app)
    );
    this.mainMenuService.ensureMenuItemsLoaded()
      .catch(error => console.error('apps-footer: could not load nav row apps', error));

    // Drives the footer help menu's "Switch accounts" option. Kept before the web-view guard so it
    // also loads at tablet width, where checkIsWebView() can briefly read true at first paint.
    this.subscriptions.push(
      this.userService.currentUserProfile$.subscribe(profile => {
        if (!profile) {
          return;
        }
        this.checkLinkedGuardian();
        this.loadImpersonatedUser();
      })
    );

    if(checkIsWebView())
      return;

    this.userService.currentUserProfile$.subscribe(
      (_: any) => {
        this.startMessageNotifications();
        this.connect();
      }
    );
  }

  startMessageNotifications() {
    this.subscriptions.push(
      this.notificationService.getAppCounts().subscribe(counts => {
        this.appCounts = counts ?? {};
      }));

    this.subscriptions.push(
      this.notificationService.getConnectionStatus().subscribe(status => {
        this.isConnected = status;
        this.isConnecting = false;
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.notificationService.disconnect();
  }

  private connect(): void {
    if(!this.userService.CurrentUserProfile)
      return;

    var userId = this.userService.CurrentUserProfile!.Id;
    this.isConnecting = true;
    this.notificationService.connect(userId);
  }
 
}
